import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Spinner,
  Text,
  Badge,
  Textarea,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useColorModeValue,
  useToast,
  useDisclosure,
  VStack,
  Card,
  CardBody,
  CardHeader,
} from "@chakra-ui/react";
import {
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  EditIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import { format, subDays } from "date-fns";
import {
  FiCalendar,
  FiCheckCircle,
  FiFileText,
  FiMove,
  FiMic,
  FiVolumeX,
  FiShare2,
} from "react-icons/fi";
import { hitBeApi } from "../../../api/api";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import TabCacheButton from "../../common/TabCacheButton";
import { VoiceAssistantMicButton } from "../../common/VoiceAssistantMicButton";
import {
  getSpeechRecognitionClass,
  getPreferredSttLang,
  startBrowserVoiceDictation,
} from "../../../utils/browserSpeechRecognition";
import { speakBrowserAssistantReply } from "../../../utils/browserSpeechSpeak";
import DeleteButtonWithConfirmation from "../utils/DeleteConfirmation";
import { appStore } from "../../../store/Store";
import ShareCollaborationModal from "../../common/ShareCollaborationModal";

const DEPTH_ACCENTS = [
  "teal.400",
  "cyan.500",
  "blue.400",
  "purple.400",
  "orange.400",
];

function parseYmd(d) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day);
}

function chartXAxisIntervalByLength(len) {
  if (!len || len <= 0) return "preserveStartEnd";
  if (len <= 16) return 0;
  if (len <= 45) return 1;
  if (len <= 90) return 2;
  if (len <= 180) return 4;
  return 6;
}

function chartWidthForPoints(points, baseWidth) {
  if (!points || points <= 0) return baseWidth;
  const pxPerPoint =
    points > 900 ? 6 : points > 600 ? 7 : points > 360 ? 8 : points > 180 ? 10 : 12;
  return Math.max(baseWidth, points * pxPerPoint);
}

function chartHeightForPoints(points, baseHeight, maxHeight = 820) {
  if (!points || points <= 0) return baseHeight;
  const extra = Math.min(maxHeight - baseHeight, Math.floor(points / 3));
  return Math.max(baseHeight, Math.min(maxHeight, baseHeight + extra));
}

function percentile(values, p) {
  if (!Array.isArray(values) || values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.floor((sorted.length - 1) * p))
  );
  return sorted[idx];
}

const TODO_ANALYTICS_RANGES = [
  { id: "30d", label: "30 days", days: 30 },
  { id: "60d", label: "60 days", days: 60 },
  { id: "90d", label: "90 days", days: 90 },
  { id: "6m", label: "6 months", days: 180 },
  { id: "all", label: "All time", days: null },
  { id: "custom", label: "Custom", days: null },
];

function buildDisplayTree(allTodos, completedSet, hideCompleted) {
  if (!Array.isArray(allTodos) || allTodos.length === 0) return [];

  const childrenMap = new Map();
  const byId = new Map();

  allTodos.forEach((todo) => {
    const id = String(todo._id);
    const parentKey = todo.parentId == null ? "__root__" : String(todo.parentId);
    if (!childrenMap.has(parentKey)) childrenMap.set(parentKey, []);
    childrenMap.get(parentKey).push(id);
    byId.set(id, {
      ...todo,
      parentId: todo.parentId ?? null,
      childrenCollapsed: !!todo.childrenCollapsed,
      notes: todo.notes != null ? String(todo.notes) : "",
    });
  });

  const memo = new Map();
  const isEffectivelyDone = (id) => {
    if (memo.has(id)) return memo.get(id);
    const kids = childrenMap.get(id) || [];
    const directDone = completedSet.has(String(id));
    const done = kids.length === 0 ? directDone : directDone || kids.every(isEffectivelyDone);
    memo.set(id, done);
    return done;
  };

  const build = (parentKey) => {
    const ids = childrenMap.get(parentKey) || [];
    const out = [];
    ids.forEach((id) => {
      const todo = byId.get(id);
      if (!todo) return;
      if (hideCompleted && isEffectivelyDone(id)) return;
      out.push({
        ...todo,
        children: build(id),
      });
    });
    return out;
  };

  return build("__root__");
}

function TodoBranch({
  node,
  depth,
  completedSet,
  onToggleDay,
  onDelete,
  onRequestAddChild,
  onToggleCollapse,
  addUnderId,
  subDraft,
  setSubDraft,
  onSubmitSubTask,
  collapseSavingId,
  editingTodoId,
  editTitleDraft,
  setEditTitleDraft,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSaveNotes,
  onDragStart,
  onDragOverRow,
  onDropOnRow,
  dragTodoId,
  isDragDisabled,
  onShareRoot,
}) {
  const id = String(node._id);
  const isEditingRow = editingTodoId === id;
  const checked = completedSet.has(id);
  const showSubInput = addUnderId === id;
  const hasChildren = (node.children?.length ?? 0) > 0;
  const collapsed = !!node.childrenCollapsed;
  const showChildren = hasChildren && !collapsed;
  const accent = DEPTH_ACCENTS[depth % DEPTH_ACCENTS.length];
  const savedNotes = String(node.notes ?? "");
  const hasSavedNote = savedNotes.trim().length > 0;
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(savedNotes);
  const [notesSaving, setNotesSaving] = useState(false);

  useEffect(() => {
    setNotesDraft(savedNotes);
  }, [node._id, savedNotes]);

  const rowBg = useColorModeValue("white", "gray.800");
  const rowBorder = useColorModeValue("gray.100", "gray.600");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const hoverBorder = useColorModeValue("teal.200", "teal.600");
  const guideLine = useColorModeValue("gray.200", "gray.600");
  const inputBg = useColorModeValue("gray.50", "gray.900");
  const subInputShell = useColorModeValue("teal.50", "rgba(45, 212, 191, 0.12)");
  const subInputBorder = useColorModeValue("teal.100", "teal.800");
  const titleColor = useColorModeValue("gray.800", "gray.100");
  const dragHandleColor = useColorModeValue("gray.500", "gray.400");
  const notesPanelBg = useColorModeValue("gray.50", "gray.900");
  const notesMuted = useColorModeValue("gray.500", "gray.400");

  const handleSaveNotesClick = async () => {
    setNotesSaving(true);
    try {
      await onSaveNotes(id, notesDraft);
      setNotesOpen(false);
    } finally {
      setNotesSaving(false);
    }
  };

  return (
    <Box position="relative">
      {depth > 0 ? (
        <Box
          position="absolute"
          left={`${Math.max(0, depth * 14 - 6)}px`}
          top={0}
          bottom={0}
          w="2px"
          bg={guideLine}
          borderRadius="full"
          opacity={0.85}
          display={{ base: "none", sm: "block" }}
          aria-hidden
        />
      ) : null}
      <Box pl={{ base: depth * 2.5, md: depth * 3.5 }} pr={0}>
        <Flex
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "flex-start" }}
          gap={{ base: 2.5, lg: 2 }}
          px={{ base: 3, md: 3 }}
          py={{ base: 3, md: 2.5 }}
          mb={2}
          borderRadius="xl"
          bg={rowBg}
          borderWidth="1px"
          borderColor={rowBorder}
          borderLeftWidth="4px"
          borderLeftColor={accent}
          boxShadow="sm"
          role="group"
          transition="all 0.2s ease"
          draggable={!isDragDisabled}
          onDragStart={(e) => onDragStart(e, id)}
          onDragOver={(e) => onDragOverRow(e)}
          onDrop={(e) => onDropOnRow(e, id)}
          opacity={dragTodoId === id ? 0.55 : 1}
          _hover={{
            boxShadow: "md",
            borderColor: hoverBorder,
            bg: rowHoverBg,
          }}
        >
          <HStack align="flex-start" spacing={2} flex="1" minW={0}>
            <Flex
              flexShrink={0}
              alignSelf="center"
              w={{ base: 9, lg: "20px" }}
              minH={10}
              align="center"
              justify="center"
              color={dragHandleColor}
              cursor={isDragDisabled ? "not-allowed" : "grab"}
              aria-label="Drag to reorder"
            >
              <Icon as={FiMove} boxSize={{ base: 5, lg: 4 }} />
            </Flex>
            <Flex flexShrink={0} alignSelf="center" justify="center">
              {hasChildren ? (
                <IconButton
                  size={{ base: "md", lg: "sm" }}
                  variant="ghost"
                  borderRadius="md"
                  aria-label={collapsed ? "Expand sub-tasks" : "Collapse sub-tasks"}
                  aria-expanded={!collapsed}
                  icon={
                    collapsed ? (
                      <ChevronRightIcon boxSize={5} />
                    ) : (
                      <ChevronDownIcon boxSize={5} />
                    )
                  }
                  isLoading={collapseSavingId === id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCollapse(id, !collapsed);
                  }}
                />
              ) : (
                <Box w={9} minH={10} aria-hidden />
              )}
            </Flex>
            <Checkbox
              alignSelf="center"
              size={{ base: "lg", lg: "md" }}
              isChecked={checked}
              isDisabled={isEditingRow}
              onChange={(e) => onToggleDay(id, e.target.checked)}
              colorScheme="teal"
              sx={{
                ".chakra-checkbox__control": {
                  borderRadius: "md",
                  borderWidth: "2px",
                },
              }}
            />
            {isEditingRow ? (
              <HStack flex="1" minW={0} spacing={2} align="center" flexWrap="wrap">
                <Input
                  size="md"
                  flex="1"
                  minW={{ base: "100%", lg: "12rem" }}
                  value={editTitleDraft}
                  bg={inputBg}
                  borderRadius="md"
                  autoFocus
                  onChange={(e) => setEditTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSaveEdit();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      onCancelEdit();
                    }
                  }}
                />
                <IconButton
                  size="md"
                  variant="solid"
                  colorScheme="teal"
                  borderRadius="md"
                  aria-label="Save title"
                  icon={<CheckIcon boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSaveEdit();
                  }}
                />
                <IconButton
                  size="md"
                  variant="ghost"
                  borderRadius="md"
                  aria-label="Cancel edit"
                  icon={<CloseIcon boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelEdit();
                  }}
                />
              </HStack>
            ) : (
              <Text
                flex="1"
                minW={0}
                fontSize="md"
                fontWeight="semibold"
                letterSpacing="-0.01em"
                lineHeight="1.4"
                color={titleColor}
                textDecoration={checked ? "line-through" : "none"}
                opacity={checked ? 0.72 : 1}
                transition="opacity 0.2s ease"
                cursor="default"
                py={{ base: 0.5, lg: 1 }}
              >
                {node.title}
              </Text>
            )}
          </HStack>

          <HStack
            spacing={{ base: 2, lg: 1 }}
            flexShrink={0}
            flexWrap="wrap"
            opacity={0.94}
            _groupHover={{ opacity: 1 }}
            justify={{ base: "flex-end", lg: "flex-end" }}
            align="center"
            w={{ base: "100%", lg: "auto" }}
            pt={{ base: 1, lg: 0 }}
            pl={{ base: 0, lg: 2 }}
            borderTopWidth={{ base: "1px", lg: 0 }}
            borderColor={rowBorder}
            rowGap={2}
          >
            {!isEditingRow ? (
              <Tooltip label={hasSavedNote ? "View or edit notes" : "Add notes"} hasArrow>
                <Box position="relative">
                  <IconButton
                    size={{ base: "md", lg: "sm" }}
                    variant={notesOpen || hasSavedNote ? "solid" : "ghost"}
                    colorScheme="teal"
                    borderRadius="lg"
                    aria-label="Task notes"
                    icon={<Icon as={FiFileText} boxSize={4} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setNotesOpen((o) => !o);
                    }}
                  />
                  {hasSavedNote && !notesOpen ? (
                    <Badge
                      position="absolute"
                      top="-4px"
                      right="-4px"
                      boxSize="14px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="full"
                      colorScheme="teal"
                      fontSize="0.55rem"
                      p={0}
                    >
                      ·
                    </Badge>
                  ) : null}
                </Box>
              </Tooltip>
            ) : null}
            {onShareRoot && !isEditingRow ? (
              <Tooltip
                label="Invite others — this task and its sub-tasks stay in sync"
                hasArrow
              >
                <IconButton
                  size={{ base: "md", lg: "sm" }}
                  variant="ghost"
                  colorScheme="purple"
                  borderRadius="lg"
                  aria-label="Share this task branch"
                  icon={<Icon as={FiShare2} boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShareRoot(id, node.title);
                  }}
                />
              </Tooltip>
            ) : null}
            {!isEditingRow ? (
              <IconButton
                size={{ base: "md", lg: "sm" }}
                variant="ghost"
                colorScheme="teal"
                borderRadius="lg"
                aria-label="Edit title"
                icon={<EditIcon boxSize={4} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartEdit(id, node.title);
                }}
              />
            ) : null}
            <IconButton
              size={{ base: "md", lg: "sm" }}
              variant="ghost"
              colorScheme="teal"
              borderRadius="lg"
              aria-label="Add sub-task"
              isDisabled={isEditingRow}
              icon={<AddIcon boxSize={4} />}
              onClick={(e) => {
                e.stopPropagation();
                onRequestAddChild(id, hasChildren && collapsed);
              }}
            />
            <DeleteButtonWithConfirmation
              onDelete={onDelete}
              itemId={id}
              size="md"
              colorScheme="red"
              ariaLabel="Delete task"
              isDisabled={isEditingRow}
              confirmationMessage="Are you sure you want to delete this task and all nested sub-tasks? This action cannot be undone."
            />
          </HStack>
        </Flex>

        <Collapse in={notesOpen} animateOpacity>
          <Box
            mt={2}
            mb={2}
            ml={{ base: 0, sm: `${Math.max(0, depth * 3.5)}` }}
            px={{ base: 2.5, sm: 3 }}
            py={3}
            borderRadius="lg"
            bg={notesPanelBg}
            borderWidth="1px"
            borderColor={rowBorder}
          >
            <Text fontSize="xs" fontWeight="semibold" color={notesMuted} mb={2}>
              Notes (optional)
            </Text>
            <Textarea
              size="sm"
              rows={4}
              value={notesDraft}
              bg={inputBg}
              borderRadius="md"
              placeholder="Details, links, or context for this task…"
              maxLength={8000}
              onChange={(e) => setNotesDraft(e.target.value)}
              isDisabled={notesSaving}
            />
            <HStack mt={2} spacing={2} justify="flex-end">
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setNotesDraft(savedNotes);
                  setNotesOpen(false);
                }}
                isDisabled={notesSaving}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                colorScheme="teal"
                onClick={handleSaveNotesClick}
                isLoading={notesSaving}
              >
                Save
              </Button>
            </HStack>
          </Box>
        </Collapse>

        {showSubInput ? (
          <Flex
            direction={{ base: "column", sm: "row" }}
            align={{ base: "stretch", sm: "center" }}
            pl={2}
            py={3}
            px={3}
            mb={3}
            gap={2}
            flexWrap="wrap"
            borderRadius="lg"
            bg={subInputShell}
            borderWidth="1px"
            borderColor={subInputBorder}
          >
            <Input
              size="md"
              placeholder="Sub-task title…"
              value={subDraft}
              bg={inputBg}
              borderRadius="md"
              flex={{ sm: "1 1 12rem" }}
              minW={0}
              w={{ base: "100%", sm: "auto" }}
              onChange={(e) => setSubDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmitSubTask();
              }}
            />
            <Button
              size="md"
              colorScheme="teal"
              borderRadius="lg"
              w={{ base: "100%", sm: "auto" }}
              onClick={onSubmitSubTask}
            >
              Add
            </Button>
            <Button
              size="md"
              variant="ghost"
              borderRadius="lg"
              w={{ base: "100%", sm: "auto" }}
              onClick={() => onRequestAddChild(null)}
            >
              Cancel
            </Button>
          </Flex>
        ) : null}

        {showChildren ? (
          <Box pt={0}>
            {node.children.map((ch) => (
              <TodoBranch
                key={String(ch._id)}
                node={ch}
                depth={depth + 1}
                completedSet={completedSet}
                onToggleDay={onToggleDay}
                onDelete={onDelete}
                onRequestAddChild={onRequestAddChild}
                onToggleCollapse={onToggleCollapse}
                addUnderId={addUnderId}
                subDraft={subDraft}
                setSubDraft={setSubDraft}
                onSubmitSubTask={onSubmitSubTask}
                collapseSavingId={collapseSavingId}
                editingTodoId={editingTodoId}
                editTitleDraft={editTitleDraft}
                setEditTitleDraft={setEditTitleDraft}
                onStartEdit={onStartEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onSaveNotes={onSaveNotes}
                onDragStart={onDragStart}
                onDragOverRow={onDragOverRow}
                onDropOnRow={onDropOnRow}
                dragTodoId={dragTodoId}
                isDragDisabled={isDragDisabled}
                onShareRoot={onShareRoot}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

const Todos = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [allTodos, setAllTodos] = useState([]);
  const [completedForViewDay, setCompletedForViewDay] = useState([]);
  const [removeDoneLoading, setRemoveDoneLoading] = useState(false);
  const [viewDay, setViewDay] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [newTitle, setNewTitle] = useState("");
  const [addUnderId, setAddUnderId] = useState(null);
  const [subDraft, setSubDraft] = useState("");
  const [collapseSavingId, setCollapseSavingId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editTitleDraft, setEditTitleDraft] = useState("");
  const [todoVoiceDraft, setTodoVoiceDraft] = useState("");
  const [todoVoiceReply, setTodoVoiceReply] = useState("");
  const [todoVoiceLoading, setTodoVoiceLoading] = useState(false);
  const [todoVoiceListening, setTodoVoiceListening] = useState(false);
  const [todoVoiceSpeaking, setTodoVoiceSpeaking] = useState(false);
  const [dragTodoId, setDragTodoId] = useState(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const submitTodoVoiceAssistantRef = useRef(async () => {});
  const todoAnalyticsModal = useDisclosure();
  const shareTodoModal = useDisclosure();
  const [shareTodoAnchor, setShareTodoAnchor] = useState(null);

  const [todoVizBusy, setTodoVizBusy] = useState(false);
  const [todoVizSeries, setTodoVizSeries] = useState([]);
  const [todoAnalyticsRange, setTodoAnalyticsRange] = useState("90d");
  const [todoAnalyticsCustomStart, setTodoAnalyticsCustomStart] = useState("");
  const [todoAnalyticsCustomEnd, setTodoAnalyticsCustomEnd] = useState("");

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const mutedLine = useColorModeValue("gray.100", "gray.700");
  const heading = useColorModeValue("teal.700", "teal.200");
  const subtle = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.600");
  const composerBg = useColorModeValue("gray.50", "gray.900");
  const emptyBorderColor = useColorModeValue("teal.200", "teal.600");
  const stripBg = useColorModeValue("gray.50", "gray.900");
  const emptyStateBg = useColorModeValue("gray.50", "gray.900");
  const assistantHeaderBg = useColorModeValue("gray.50", "gray.900");
  const todoAssistantHeading = useColorModeValue("teal.600", "teal.300");
  const todoAssistantText = useColorModeValue("gray.800", "gray.200");
  const todoAssistantInputBg = useColorModeValue("gray.50", "gray.900");
  const todoAssistantFocus = useColorModeValue("teal.500", "teal.300");
  const todoAssistantBreadcrumbBg = useColorModeValue("gray.100", "gray.700");
  const calendarIconBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const todoChartAxis = useColorModeValue("#64748b", "#94a3b8");
  const todoChartGrid = useColorModeValue("#e2e8f033", "#ffffff14");

  const prevDayForVoice = useMemo(
    () => format(subDays(parseYmd(viewDay), 1), "yyyy-MM-dd"),
    [viewDay]
  );

  const completedSet = useMemo(
    () => new Set(completedForViewDay.map(String)),
    [completedForViewDay]
  );

  const displayTree = useMemo(
    () => buildDisplayTree(allTodos, completedSet, false),
    [allTodos, completedSet]
  );
  const totalTodoCount = allTodos.length;
  const doneTodayCount = completedSet.size;

  const load = useCallback(async () => {
    const vd = format(new Date(), "yyyy-MM-dd");
    const pd = format(subDays(parseYmd(vd), 1), "yyyy-MM-dd");
    setViewDay(vd);
    setLoading(true);
    try {
      const res = await hitBeApi(
        `todos?viewDay=${encodeURIComponent(vd)}&prevDay=${encodeURIComponent(
          pd
        )}`,
        "GET"
      );
      if (!res?.success) {
        throw new Error(res?.message || "Failed to load todos.");
      }
      const d = res.data || {};
      setAllTodos(Array.isArray(d.allTodos) ? d.allTodos : []);
      setCompletedForViewDay(
        Array.isArray(d.completedForViewDay) ? d.completedForViewDay : []
      );
    } catch (e) {
      toast({
        title: "Could not load todos",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadTodoAnalytics = useCallback(async () => {
    setTodoVizBusy(true);
    try {
      const range = TODO_ANALYTICS_RANGES.find(
        (r) => r.id === todoAnalyticsRange
      );
      let start = "";
      let end = "";
      if (range?.id === "custom") {
        start = todoAnalyticsCustomStart;
        end = todoAnalyticsCustomEnd;
      } else if (range?.days) {
        end = format(new Date(), "yyyy-MM-dd");
        start = format(subDays(parseYmd(end), range.days - 1), "yyyy-MM-dd");
      } else if (range?.id === "all") {
        end = format(new Date(), "yyyy-MM-dd");
        start = "2000-01-01";
      }
      if (!start || !end) {
        setTodoVizSeries([]);
        return;
      }
      const res = await hitBeApi(
        `todos/analytics/completions-by-day?dateFrom=${encodeURIComponent(
          start
        )}&dateTo=${encodeURIComponent(end)}&_cb=${Date.now()}`,
        "GET"
      );
      if (res?.success) {
        setTodoVizSeries(Array.isArray(res.data?.series) ? res.data.series : []);
      } else setTodoVizSeries([]);
    } catch {
      setTodoVizSeries([]);
    } finally {
      setTodoVizBusy(false);
    }
  }, [todoAnalyticsRange, todoAnalyticsCustomEnd, todoAnalyticsCustomStart]);

  useEffect(() => {
    if (todoAnalyticsModal.isOpen) {
      loadTodoAnalytics();
    }
  }, [loadTodoAnalytics, todoAnalyticsModal.isOpen]);

  const todoVizDisplayMax = useMemo(() => {
    if (!todoVizSeries.length) return 0;
    const values = todoVizSeries.map((row) => Number(row.completions) || 0);
    const p95 = percentile(values, 0.95);
    return Math.max(p95, 1);
  }, [todoVizSeries]);

  const todoChartHeight = useMemo(
    () => chartHeightForPoints(todoVizSeries.length, 420, 1200),
    [todoVizSeries.length]
  );

  const handleToggleDay = async (todoId, done) => {
    const vd = viewDay;
    try {
      const res = await hitBeApi(`todos/${todoId}/day-completion`, "POST", {
        day: vd,
        done,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Update failed.");
      }
      setCompletedForViewDay((prev) => {
        const s = new Set(prev.map(String));
        if (done) s.add(String(todoId));
        else s.delete(String(todoId));
        return [...s];
      });
      await loadTodoAnalytics();
    } catch (e) {
      toast({
        title: "Could not update",
        description: e?.message || "Try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateRoot = async () => {
    const title = String(newTitle).trim();
    if (!title) {
      toast({ title: "Enter a title", status: "warning", duration: 2000 });
      return;
    }
    try {
      const res = await hitBeApi("todos", "POST", { title });
      if (!res?.success) {
        throw new Error(res?.message || "Could not create.");
      }
      setNewTitle("");
      await load();
    } catch (e) {
      toast({
        title: "Could not create task",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleSubmitSubTask = async () => {
    if (!addUnderId) return;
    const title = String(subDraft).trim();
    if (!title) {
      toast({
        title: "Enter a sub-task title",
        status: "warning",
        duration: 2000,
      });
      return;
    }
    try {
      const res = await hitBeApi("todos", "POST", {
        title,
        parentId: addUnderId,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Could not create.");
      }
      setSubDraft("");
      setAddUnderId(null);
      await load();
    } catch (e) {
      toast({
        title: "Could not create sub-task",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const cancelTitleEdit = () => {
    setEditingTodoId(null);
    setEditTitleDraft("");
  };

  const handleStartTodoEdit = (id, title) => {
    setEditingTodoId(String(id));
    setEditTitleDraft(String(title ?? ""));
  };

  const handleSaveTodoTitle = async () => {
    if (!editingTodoId) return;
    const title = String(editTitleDraft).trim();
    if (!title) {
      toast({
        title: "Enter a title",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      const res = await hitBeApi(`todos/${editingTodoId}`, "PUT", { title });
      if (!res?.success) {
        throw new Error(res?.message || "Could not save.");
      }
      cancelTitleEdit();
      await load();
    } catch (e) {
      toast({
        title: "Could not update task",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleSaveTodoNotes = async (todoId, notes) => {
    try {
      const res = await hitBeApi(`todos/${todoId}`, "PUT", {
        notes: String(notes ?? "").slice(0, 8000),
      });
      if (!res?.success) {
        throw new Error(res?.message || "Could not save.");
      }
      toast({
        title: "Notes saved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      await load();
    } catch (e) {
      toast({
        title: "Could not save notes",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      throw e;
    }
  };

  const onRequestAddChild = async (id, needsExpandFirst) => {
    if (id == null) {
      setAddUnderId(null);
      setSubDraft("");
      return;
    }
    cancelTitleEdit();
    if (needsExpandFirst) {
      setCollapseSavingId(String(id));
      try {
        const res = await hitBeApi(`todos/${id}`, "PUT", {
          childrenCollapsed: false,
        });
        if (!res?.success) {
          throw new Error(res?.message || "Could not expand.");
        }
        await load();
      } catch (e) {
        toast({
          title: "Could not expand",
          description: e?.message || "Try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setCollapseSavingId(null);
        return;
      }
      setCollapseSavingId(null);
    }
    setAddUnderId(id);
    setSubDraft("");
  };

  const handleToggleCollapse = async (todoId, nextCollapsed) => {
    setCollapseSavingId(String(todoId));
    try {
      const res = await hitBeApi(`todos/${todoId}`, "PUT", {
        childrenCollapsed: nextCollapsed,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Could not update.");
      }
      await load();
    } catch (e) {
      toast({
        title: "Could not update",
        description: e?.message || "Try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCollapseSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (String(editingTodoId) === String(id)) {
      cancelTitleEdit();
    }
    try {
      const res = await hitBeApi(`todos/${id}`, "DELETE");
      if (!res?.success) {
        throw new Error(res?.message || "Delete failed.");
      }
      await load();
    } catch (e) {
      toast({
        title: "Could not delete",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleRemoveDoneNow = async () => {
    const doneIds = [...new Set(completedForViewDay.map(String))];
    if (doneIds.length === 0) {
      toast({
        title: "No done tasks to remove",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setRemoveDoneLoading(true);
    try {
      const results = await Promise.allSettled(
        doneIds.map((id) => hitBeApi(`todos/${id}`, "DELETE"))
      );
      const successCount = results.filter(
        (r) => r.status === "fulfilled" && r.value?.success
      ).length;
      if (successCount === 0) {
        throw new Error("Could not remove completed tasks.");
      }
      await load();
      toast({
        title: "Done tasks removed",
        description:
          successCount === doneIds.length
            ? "Completed tasks were removed and will not be shown again."
            : `Removed ${successCount} of ${doneIds.length} completed tasks.`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Could not remove done tasks",
        description: e?.message || "Try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setRemoveDoneLoading(false);
    }
  };

  const handleDragStart = (e, todoId) => {
    if (editingTodoId || reorderSaving) return;
    setDragTodoId(String(todoId));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverRow = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropOnRow = async (e, targetId) => {
    e.preventDefault();
    const draggedId = String(dragTodoId || "");
    const normalizedTargetId = String(targetId || "");
    setDragTodoId(null);
    if (!draggedId || !normalizedTargetId || draggedId === normalizedTargetId) return;

    setReorderSaving(true);
    try {
      const res = await hitBeApi(`todos/${draggedId}/reorder`, "POST", {
        targetId: normalizedTargetId,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Could not reorder.");
      }
      await load();
    } catch (e2) {
      toast({
        title: "Could not reorder task",
        description: e2?.message || "Try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setReorderSaving(false);
    }
  };

  const stopTodoAssistantSpeech = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setTodoVoiceSpeaking(false);
  }, []);

  const speakTodoAssistantReply = useCallback((replyText) => {
    if (typeof window === "undefined" || !replyText) return;
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      speakBrowserAssistantReply(replyText, {
        lang: "en-IN",
        flushDelayMs: 0,
        onBeforeSpeak: () => setTodoVoiceSpeaking(true),
        onEnd: () => setTodoVoiceSpeaking(false),
        onError: () => setTodoVoiceSpeaking(false),
      });
    } catch {
      setTodoVoiceSpeaking(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const runTodoVoiceCaptureOnce = () => {
    stopTodoAssistantSpeech();
    startBrowserVoiceDictation({
      lang: getPreferredSttLang(),
      onListeningStart: () => setTodoVoiceListening(true),
      onInterim: (t) => setTodoVoiceDraft(t),
      onListeningEnd: () => setTodoVoiceListening(false),
      onFinal: (t) => {
        setTodoVoiceDraft(t);
        submitTodoVoiceAssistantRef.current(t);
      },
      onError: () => {},
    });
  };

  const submitTodoVoiceAssistant = async (overrideText) => {
    const raw =
      overrideText !== undefined && overrideText !== null
        ? String(overrideText)
        : todoVoiceDraft;
    const text = raw.trim();
    if (!text) {
      toast({
        title: "Nothing to send",
        description: "Type or dictate a command.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    stopTodoAssistantSpeech();
    setTodoVoiceLoading(true);
    try {
      const res = await hitBeApi("todos/voice-assistant", "POST", {
        text,
        viewDay,
        prevDay: prevDayForVoice,
      });
      if (res.success && res.data) {
        if (res.data.userTokenDetails) {
          const currentUser = appStore.getState().user || {};
          appStore.dispatch({
            type: "SET_USER",
            value: { ...currentUser, userTokenDetails: res.data.userTokenDetails },
          });
        }
        const reply = res.data.reply || "";
        setTodoVoiceReply(reply);
        if (res.data.actions?.length) {
          await load();
        }
        const hasMutation = res.data.actions?.length > 0;
        toast({
          title: hasMutation ? "Todos updated" : "Assistant",
          description: reply || "Done.",
          status: hasMutation ? "success" : "info",
          duration: 4500,
          isClosable: true,
        });
        if (reply) {
          speakTodoAssistantReply(reply);
        }
      }
    } catch (error) {
      toast({
        title: "Assistant error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setTodoVoiceLoading(false);
    }
  };

  submitTodoVoiceAssistantRef.current = submitTodoVoiceAssistant;

  const startTodoVoiceRecognition = () => {
    if (!getSpeechRecognitionClass()) {
      toast({
        title: "Voice not supported",
        description: "Use Chrome or Edge, or type below.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (todoVoiceLoading || todoVoiceListening) return;
    runTodoVoiceCaptureOnce();
  };

  return (
    <Box bg={pageBg} py={{ base: 3, md: 8 }}>
      <Container maxW="container.md" px={{ base: 0, md: 4 }}>
        <VStack align="stretch" spacing={{ base: 5, md: 7 }}>
          <Card
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={cardBorder}
            bg={cardBg}
          >
            <Box
              bgGradient="linear(to-r, teal.500, cyan.500)"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 7 }}
            >
              <Badge
                colorScheme="whiteAlpha"
                bg="whiteAlpha.300"
                color="white"
                fontSize="0.65rem"
                textTransform="uppercase"
                letterSpacing="0.12em"
                mb={3}
                borderRadius="md"
              >
                Today&apos;s focus
              </Badge>
              <Flex
                align="center"
                justify="space-between"
                gap={3}
                flexWrap="wrap"
                w="100%"
                maxW="3xl"
              >
                <Heading
                  as="h1"
                  size="lg"
                  color="white"
                  fontWeight="extrabold"
                  letterSpacing="tight"
                  lineHeight="1.2"
                >
                  Daily todos
                </Heading>
                <TabCacheButton tabId="todos" onGradient />
              </Flex>
              <Text
                fontSize="sm"
                color="whiteAlpha.900"
                mt={3}
                maxW="2xl"
                lineHeight="tall"
                display={{ base: "none", md: "block" }}
              >
                Check items when done for today. Use the arrow to collapse a parent and
                hide its sub-tasks (saved for next time). Use + for nested tasks. Drag
                tasks by the grip icon to reorder.
              </Text>
              <Text fontSize="xs" color="whiteAlpha.850" mt={3} maxW="2xl" lineHeight="tall" display={{ base: "block", md: "none" }}>
                Tap ✓ when done today. Expand or collapse parents with chevrons. Actions sit under each task; drag the grip to reorder.
              </Text>
              <Button
                size="sm"
                variant="link"
                color="white"
                mt={4}
                fontWeight="semibold"
                _hover={{ color: "whiteAlpha.800" }}
                onClick={() => setShowHelp((v) => !v)}
              >
                {showHelp ? "Hide" : "Show"} how visibility works
              </Button>
              <Collapse in={showHelp}>
                <Text
                  fontSize="xs"
                  color="whiteAlpha.900"
                  mt={3}
                  lineHeight="tall"
                  maxW="2xl"
                  bg="blackAlpha.200"
                  p={3}
                  borderRadius="lg"
                >
                  Each morning, rows that were fully complete yesterday stay hidden (you
                  checked them, or for a parent every sub-task was checked). Everything
                  else stays in your list.
                </Text>
              </Collapse>
            </Box>
          </Card>

          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={cardBorder}
            bg={cardBg}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardHeader
              pb={2}
              borderBottomWidth="1px"
              borderColor={mutedLine}
              bg={assistantHeaderBg}
            >
              <HStack spacing={3}>
                <Icon as={FiMic} color={todoAssistantHeading} boxSize={5} />
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color={todoAssistantHeading}>
                    Voice &amp; text assistant
                  </Text>
                  <Text fontSize="xs" color={subtle}>
                    Natural-language commands for your list.
                  </Text>
                </Box>
              </HStack>
            </CardHeader>
            <CardBody px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
              <Text fontSize="xs" color={subtle} mb={3} lineHeight="tall">
                Add, rename, delete, mark done for today, or collapse sub-tasks. Examples:
                &quot;Add task buy milk&quot;, &quot;Rename gym to gym workout&quot;,
                &quot;Mark standup as done&quot;, &quot;Collapse sub-tasks under project
                alpha&quot;.
              </Text>
              <Textarea
                value={todoVoiceDraft}
                onChange={(e) => setTodoVoiceDraft(e.target.value)}
                placeholder='Example: "Add subtask call dad under Work"'
                size="sm"
                rows={3}
                bg={todoAssistantInputBg}
                borderColor={cardBorder}
                borderRadius="lg"
                _focus={{
                  borderColor: todoAssistantFocus,
                  boxShadow: "outline",
                }}
                mb={3}
              />
              <VStack align="stretch" spacing={3}>
                <Flex direction={{ base: "column", sm: "row" }} gap={3} align="stretch" flexWrap="wrap">
                  <Button
                    size="sm"
                    colorScheme="teal"
                    w={{ base: "100%", sm: "auto" }}
                    onClick={() => submitTodoVoiceAssistant()}
                    isLoading={todoVoiceLoading}
                    loadingText="Sending"
                  >
                    Send to assistant
                  </Button>
                  <VoiceAssistantMicButton
                    compact
                    colorScheme="teal"
                    tooltipLabel="Browser speech (Chrome / Edge). Sends text to the server."
                    onClick={startTodoVoiceRecognition}
                    isLoading={todoVoiceListening}
                    isDisabled={todoVoiceLoading}
                  />
                  {todoVoiceSpeaking ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      leftIcon={<Icon as={FiVolumeX} />}
                      w={{ base: "100%", sm: "auto" }}
                      onClick={stopTodoAssistantSpeech}
                    >
                      Stop speaking
                    </Button>
                  ) : null}
                </Flex>
                {(todoVoiceListening || todoVoiceSpeaking) && (
                  <HStack spacing={2}>
                    {todoVoiceListening ? (
                      <Badge colorScheme="teal" variant="subtle">
                        Listening
                      </Badge>
                    ) : null}
                    {todoVoiceSpeaking ? (
                      <Badge colorScheme="purple" variant="subtle">
                        Speaking
                      </Badge>
                    ) : null}
                  </HStack>
                )}
              </VStack>
              {todoVoiceReply ? (
                <Box
                  mt={4}
                  p={3}
                  borderRadius="lg"
                  bg={todoAssistantBreadcrumbBg}
                  borderWidth="1px"
                  borderColor={cardBorder}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={todoAssistantHeading}
                    mb={1}
                  >
                    Assistant
                  </Text>
                  <Text fontSize="sm" color={todoAssistantText} whiteSpace="pre-wrap">
                    {todoVoiceReply}
                  </Text>
                </Box>
              ) : null}
            </CardBody>
          </Card>

          <Box>
            <Button
              colorScheme="teal"
              variant="solid"
              size="md"
              borderRadius="lg"
              w={{ base: "100%", sm: "auto" }}
              onClick={todoAnalyticsModal.onOpen}
            >
              Show analytics · completed check-ins
            </Button>
            <Text fontSize="xs" color={subtle} mt={2}>
              Opens a full-screen view of your completion trend.
            </Text>
          </Box>

          <Modal
            isOpen={todoAnalyticsModal.isOpen}
            onClose={todoAnalyticsModal.onClose}
            size="2xl"
            motionPreset="scale"
            scrollBehavior="inside"
            closeOnOverlayClick={false}
            isCentered
          >
            <ModalOverlay bg="blackAlpha.500" />
            <ModalContent
              mx={{ base: 3, md: 6 }}
              borderRadius="2xl"
              bg={cardBg}
              borderWidth="1px"
              borderColor={cardBorder}
              maxW="min(92vw, 980px)"
              display="flex"
              flexDirection="column"
              boxShadow="2xl"
            >
              <ModalHeader
                borderBottomWidth="1px"
                borderColor={mutedLine}
                bg={stripBg}
                py={4}
                px={{ base: 4, md: 6 }}
              >
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                  <Box>
                    <Text fontWeight="semibold" color={heading}>
                      Completed check-ins · last ~90 days
                    </Text>
                    <Text fontSize="xs" color={subtle} mt={1}>
                      Daily count of todos you marked done (any day streak).
                    </Text>
                    <HStack mt={3} spacing={2} flexWrap="wrap">
                      {TODO_ANALYTICS_RANGES.map((range) => (
                        <Button
                          key={range.id}
                          size="sm"
                          variant={
                            todoAnalyticsRange === range.id ? "solid" : "outline"
                          }
                          colorScheme="teal"
                          borderRadius="full"
                          onClick={() => setTodoAnalyticsRange(range.id)}
                        >
                          {range.label}
                        </Button>
                      ))}
                    </HStack>
                    {todoAnalyticsRange === "custom" ? (
                      <HStack mt={3} spacing={3} flexWrap="wrap" align="flex-end">
                        <FormControl maxW="180px">
                          <FormLabel fontSize="xs" color={subtle}>
                            From
                          </FormLabel>
                          <Input
                            type="date"
                            size="sm"
                            value={todoAnalyticsCustomStart}
                            onChange={(e) =>
                              setTodoAnalyticsCustomStart(e.target.value)
                            }
                            bg={todoAssistantInputBg}
                          />
                        </FormControl>
                        <FormControl maxW="180px">
                          <FormLabel fontSize="xs" color={subtle}>
                            To
                          </FormLabel>
                          <Input
                            type="date"
                            size="sm"
                            value={todoAnalyticsCustomEnd}
                            onChange={(e) =>
                              setTodoAnalyticsCustomEnd(e.target.value)
                            }
                            bg={todoAssistantInputBg}
                          />
                        </FormControl>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          isDisabled={
                            !todoAnalyticsCustomStart || !todoAnalyticsCustomEnd
                          }
                          onClick={() => setTodoAnalyticsRange("custom")}
                        >
                          Apply
                        </Button>
                      </HStack>
                    ) : null}
                  </Box>
                  <Button size="sm" colorScheme="red" onClick={todoAnalyticsModal.onClose}>
                    Close
                  </Button>
                </Flex>
              </ModalHeader>
              <ModalBody
                flex="1"
                minH={0}
                display="flex"
                flexDirection="column"
                p={{ base: 5, md: 6 }}
                overflowY="auto"
              >
                {todoVizBusy && todoVizSeries.length === 0 ? (
                  <Flex justify="center" py={8}>
                    <Spinner color="teal.400" />
                  </Flex>
                ) : todoVizSeries.length ? (
                  <Box
                    flex="1"
                    minH={{
                      base: `${todoChartHeight}px`,
                      md: `${todoChartHeight}px`,
                    }}
                    pl={{ base: 4, md: 5 }}
                    pr={{ base: 2, md: 3 }}
                    pt={{ base: 3, md: 4 }}
                  >
                    <Box w="100%" h="100%" overflowX="auto" overflowY="visible" pb={6}>
                      <Box
                        w={chartWidthForPoints(todoVizSeries.length, 800)}
                        h={todoChartHeight}
                      >
                        <AreaChart
                          width={chartWidthForPoints(todoVizSeries.length, 800)}
                          height={todoChartHeight}
                          data={todoVizSeries}
                          margin={{ top: 24, right: 24, bottom: 30, left: 52 }}
                        >
                        <defs>
                          <linearGradient id="todoDoneGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.85} />
                            <stop offset="100%" stopColor="#14b8a6" stopOpacity={0.08} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="5 10" stroke={todoChartGrid} />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 10, fill: todoChartAxis }}
                          tickFormatter={(d) => (typeof d === "string" ? d.slice(5) : d)}
                          interval={chartXAxisIntervalByLength(todoVizSeries.length)}
                          minTickGap={14}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: todoChartAxis }}
                          allowDecimals={false}
                          domain={[0, todoVizDisplayMax]}
                          allowDataOverflow
                          tickMargin={8}
                        />
                        <RechartsTooltip
                          formatter={(val) =>
                            typeof val === "number" ? val.toLocaleString("en-IN") : val
                          }
                          contentStyle={{ borderRadius: 12 }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="completions"
                          name="Marked done"
                          stroke="#0d9488"
                          strokeWidth={2}
                          fill="url(#todoDoneGrad)"
                        />
                        </AreaChart>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Text fontSize="sm" color={subtle} textAlign="center" py={6}>
                    Tick a todo for today — your streak chart will populate here.
                  </Text>
                )}
              </ModalBody>
              <ModalFooter borderTopWidth="1px" borderColor={mutedLine} bg={stripBg}>
                <Text fontSize="xs" color={subtle}>
                  Tip: press Esc to close.
                </Text>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Card
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
            bg={cardBg}
            boxShadow="md"
            overflow="hidden"
          >
            <CardHeader
              py={{ base: 4, md: 5 }}
              borderBottomWidth="1px"
              borderColor={mutedLine}
              bg={stripBg}
            >
              <Flex
                direction={{ base: "column", lg: "row" }}
                align={{ base: "stretch", lg: "center" }}
                justify="space-between"
                gap={4}
              >
                <HStack spacing={3} align="flex-start" flex="1" minW={0}>
                  <Flex
                    align="center"
                    justify="center"
                    w={10}
                    h={10}
                    flexShrink={0}
                    borderRadius="xl"
                    bg={calendarIconBg}
                    color="teal.500"
                  >
                    <Icon as={FiCalendar} boxSize={5} />
                  </Flex>
                  <Box minW={0}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color={subtle}
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                    >
                      Working day
                    </Text>
                    <Heading as="h2" size="md" color={heading} fontWeight="bold">
                      {format(parseYmd(viewDay), "EEEE")}
                    </Heading>
                    <Text fontSize="sm" color={subtle}>
                      {format(parseYmd(viewDay), "d MMMM yyyy")}
                    </Text>
                    <Flex gap={2} mt={2} flexWrap="wrap">
                      <Badge colorScheme="teal" variant="subtle" borderRadius="md">
                        Total: {totalTodoCount.toLocaleString("en-IN")}
                      </Badge>
                      <Badge colorScheme="green" variant="subtle" borderRadius="md">
                        Done today: {doneTodayCount.toLocaleString("en-IN")}
                      </Badge>
                    </Flex>
                  </Box>
                </HStack>
                <Flex direction={{ base: "column", sm: "row" }} gap={2} w={{ base: "100%", lg: "auto" }} flexShrink={0}>
                  <Button
                    size="sm"
                    leftIcon={<RepeatIcon />}
                    variant="outline"
                    colorScheme="teal"
                    borderRadius="lg"
                    flex={{ lg: "none" }}
                    w={{ base: "100%", sm: "auto" }}
                    onClick={() => load()}
                  >
                    Refresh
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    borderRadius="lg"
                    flex={{ lg: "none" }}
                    w={{ base: "100%", sm: "auto" }}
                    onClick={handleRemoveDoneNow}
                    isLoading={removeDoneLoading}
                    loadingText="Removing"
                  >
                    Remove done now
                  </Button>
                </Flex>
              </Flex>
            </CardHeader>

            <CardBody px={{ base: 4, md: 6 }} pt={6} pb={2}>
              <Text
                fontSize="xs"
                fontWeight="bold"
                color={subtle}
                textTransform="uppercase"
                letterSpacing="0.08em"
                mb={3}
              >
                New task
              </Text>
              <Flex
                direction={{ base: "column", sm: "row" }}
                gap={3}
                align={{ base: "stretch", sm: "center" }}
                p={{ base: 3, sm: 2 }}
                pl={{ base: 3, sm: 4 }}
                pr={{ base: 3, sm: 2 }}
                py={{ base: 3, sm: 2 }}
                borderRadius="xl"
                bg={composerBg}
                borderWidth="1px"
                borderColor={cardBorder}
              >
                <Input
                  placeholder="What needs your attention?"
                  value={newTitle}
                  size="md"
                  borderRadius="lg"
                  borderWidth="0"
                  bg="transparent"
                  flex={1}
                  minW={0}
                  _focus={{ boxShadow: "none", borderColor: "transparent" }}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateRoot();
                  }}
                />
                <Button
                  colorScheme="teal"
                  borderRadius="lg"
                  px={6}
                  size="md"
                  leftIcon={<AddIcon />}
                  onClick={handleCreateRoot}
                  flexShrink={0}
                  w={{ base: "100%", sm: "auto" }}
                >
                  Add
                </Button>
              </Flex>
            </CardBody>

            <Divider borderColor={mutedLine} />

            <CardBody px={{ base: 4, md: 6 }} py={6} pb={8}>
              {loading ? (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py={16}
                  gap={4}
                >
                  <Spinner
                    thickness="3px"
                    speed="0.7s"
                    color="teal.500"
                    size="lg"
                  />
                  <Text fontSize="sm" color={subtle}>
                    Loading your list…
                  </Text>
                </Flex>
              ) : displayTree.length === 0 ? (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py={12}
                  px={4}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderColor={emptyBorderColor}
                  bg={emptyStateBg}
                >
                  <Icon
                    as={FiCheckCircle}
                    boxSize={12}
                    color="teal.300"
                    mb={4}
                  />
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={heading}
                    textAlign="center"
                  >
                    All clear for now
                  </Text>
                  <Text
                    fontSize="sm"
                    color={subtle}
                    mt={2}
                    textAlign="center"
                    maxW="md"
                    lineHeight="tall"
                  >
                    Nothing open from yesterday&apos;s perspective, or you&apos;re starting
                    fresh. Add a task above — use + on a row for sub-tasks.
                  </Text>
                </Flex>
              ) : (
                <VStack align="stretch" spacing={0}>
                  {displayTree.map((node) => (
                    <TodoBranch
                      key={String(node._id)}
                      node={node}
                      depth={0}
                      completedSet={completedSet}
                      onToggleDay={handleToggleDay}
                      onDelete={handleDelete}
                      onRequestAddChild={onRequestAddChild}
                      onToggleCollapse={handleToggleCollapse}
                      addUnderId={addUnderId}
                      subDraft={subDraft}
                      setSubDraft={setSubDraft}
                      onSubmitSubTask={handleSubmitSubTask}
                      collapseSavingId={collapseSavingId}
                      editingTodoId={editingTodoId}
                      editTitleDraft={editTitleDraft}
                      setEditTitleDraft={setEditTitleDraft}
                      onStartEdit={handleStartTodoEdit}
                      onSaveEdit={handleSaveTodoTitle}
                      onCancelEdit={cancelTitleEdit}
                      onSaveNotes={handleSaveTodoNotes}
                      onDragStart={handleDragStart}
                      onDragOverRow={handleDragOverRow}
                      onDropOnRow={handleDropOnRow}
                      dragTodoId={dragTodoId}
                      isDragDisabled={!!editingTodoId || reorderSaving}
                      onShareRoot={(tid, title) => {
                        setShareTodoAnchor({ id: tid, title });
                        shareTodoModal.onOpen();
                      }}
                    />
                  ))}
                </VStack>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Container>

      <ShareCollaborationModal
        isOpen={shareTodoModal.isOpen}
        onClose={() => {
          shareTodoModal.onClose();
          setShareTodoAnchor(null);
        }}
        titleLabel={
          shareTodoAnchor
            ? `Share todos — “${shareTodoAnchor.title}”`
            : "Share todos"
        }
        kind="TODO"
        anchor={{
          todoAnchorId: shareTodoAnchor?.id
            ? String(shareTodoAnchor.id)
            : undefined,
        }}
      />
    </Box>
  );
};

export default Todos;
